import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaAngleRight } from 'react-icons/fa';
import { MdLocationOn, MdMyLocation } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { geocodePlace, reverseGeocodeCity } from '../utils/geocode';

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  marginTop: '20px',
};

const defaultCenter = [19.0760, 72.8777]; // Default to Mumbai

function PanToLocation({ position }) {
  const map = useMap();
  if (position) {
    map.setView(position, 10);
  }
  return null;
}

function DeliveryLocation() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('selectedLocation') || '');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  // Sample location data
  const locations = [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Ahmedabad', 'Chennai', 'Kolkata',
    'Surat', 'Pune', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore', 'Thane',
    'Bhopal', 'Visakhapatnam', 'Patna', 'Vadodara', 'Ghaziabad',
  ];

  // Handle live location
  const handleLiveLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const pos = [position.coords.latitude, position.coords.longitude];
            setUserLocation(pos);

            const city = await reverseGeocodeCity(pos[0], pos[1]);

            if (city && locations.includes(city)) {
              setSelectedLocation(city);
              setSearchTerm(city);
              toast.success(`Location set to ${city}`);
              localStorage.setItem('selectedLocation', city);
            } else {
              toast.error('City not in supported list. Select manually.');
            }
          } catch (error) {
            // Surface the reason when we have one — "rate limited" and "no network"
            // need different responses from the user, and the old message implied
            // their location was the problem in both cases.
            toast.error(error?.message || 'Error getting location. Please select manually.');
          }
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            toast.error(
              'Location access blocked. Reset permissions in browser settings (click the lock/tune icon next to the URL) and try again.',
              { autoClose: 5000 }
            );
          } else {
            toast.error('Please enable location access or select manually');
          }
        },
        { enableHighAccuracy: true }
      );
    } else {
      toast.error('Geolocation not supported. Please select manually');
    }
  };

  // Handle location selection
  const handleLocationSelect = async (location) => {
    setSelectedLocation(location);
    toast.success(`Location set to ${location}`);
    localStorage.setItem('selectedLocation', location);
    setSearchTerm(location);

    // Geocode city to update map. Cached and rate-limited — see utils/geocode.js.
    // The location is already selected and stored above, so a geocoding failure
    // only means the map does not re-centre; it must not block the selection.
    try {
      const pos = await geocodePlace(location);
      if (pos) {
        setUserLocation(pos);
      }
    } catch (error) {
      console.error('Error geocoding city:', error);
    }
  };

  // Filter locations based on search
  const filteredLocations = locations.filter((location) =>
    location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <section className="delivery-location">
      <div className="delivery-location-header">
        <div className="container d-flex">
          <div onClick={() => navigate('/')}>
            <FaArrowLeft style={{ fontSize: '18px', fontWeight: '500' }} />
          </div>
          <h4>Change delivery location</h4>
        </div>
      </div>

      <div className="location-searchbar">
        <h4>Search for your city</h4>
        <hr />
        <div className="small-conatiner">
          <div className="search-container">
            <MdLocationOn className="search-icon" style={{ color: '#0165AB' }} />
            <input
              type="text"
              placeholder="Search for your city"
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="select-live-location d-flex" onClick={handleLiveLocation}>
        <div className="live-location-icon">
          <MdMyLocation style={{ fontSize: '24px', color: '#0165AB' }} />
        </div>
        <h4>Use your current location</h4>
      </div>

      <hr style={{ color: '#e7e7e7' }} />

      <h4>Cities</h4>

      <ul className="location-cities">
        {filteredLocations.map((location, index) => (
          <li
            key={index}
            onClick={() => handleLocationSelect(location)}
            style={{
              cursor: 'pointer',
              padding: '10px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              backgroundColor: selectedLocation === location ? '#e7f3ff' : 'transparent',
            }}
          >
            <span>{location}</span>
            <FaAngleRight />
          </li>
        ))}
      </ul>

      <div className="map-container">
        <h4>Your Location</h4>
        <MapContainer
          style={mapContainerStyle}
          center={userLocation || defaultCenter}
          zoom={10}
          scrollWheelZoom={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {userLocation && (
            <>
              <Marker position={userLocation}>
                <Popup>
                  <p>{selectedLocation || 'Your Location'}</p>
                  <p>Lat: {userLocation[0].toFixed(4)}</p>
                  <p>Lng: {userLocation[1].toFixed(4)}</p>
                </Popup>
              </Marker>
              <PanToLocation position={userLocation} />
            </>
          )}
        </MapContainer>
      </div>
    </section>
  );
}

export default DeliveryLocation;